import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertBooleanKey,
  AlertSettings,
  AlertSettingsState,
  getAlertSettings,
  saveAlertSettings,
  sendSummaryNow,
} from '../../../../api/stays-alerts-client';
import { StaysApiError } from '../../../../api/stays-client';
import type { StaysListingHead } from '../../../../interfaces/stays';
import { AppButton } from '../../../button/app-button';
import { ClockNote } from '../../../clock-note/clock-note';
import { Field, FieldError, FieldHint, Input, Label } from '../../../form-kit';
import { ChipGroup, ToggleChip } from '../../../toggle-chip/toggle-chip';
import { InlineMessage, StateSwitch } from '../../../../motion';
import toast from '../../../../utils/toast';
import { AlertTemplateKind } from '../../../../utils/stays-template-placeholders';
import { ActionWithReason } from '../../dashboard/dashboard-shared-components/disabled-reason';
import { FormSkeleton, LoadError } from '../../dashboard/dashboard-shared-components/load-state';
import { refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import { buildingClock } from '../stays-reservations-model';
import { AlertHistory } from './alert-history';
import { TemplateStudio } from './template-studio';
import { Card, CardTitle, Check, Column, Columns, Footer, GroupLabel, Page, SlotRow } from './alerts.styled';

/**
 * A LISTING'S ALERTS TAB (plan S8, SC-1; appended to S4's STAYS_LISTING_TABS).
 *
 *   Daily summary — on/off, one to three send times on the BUILDING's clock,
 *     a greeting, which sections and details the summary carries, and "Send
 *     today's summary now".
 *   Alerts — the checkout / cleaning alerts (a master switch over three) and
 *     the stay alerts.
 *   Message studio — the four templated alerts (template-studio.tsx).
 *   Recent alerts — the dispatch history (alert-history.tsx).
 *
 * ONE SAVE, PARTIAL. Only the keys that differ from what was loaded are sent,
 * with the version read; a 409 says someone else saved and offers Reload —
 * never a silent overwrite (the Konios partial-patch wipe is the server's to
 * refuse, and this form never sends an untouched key).
 */

const NO_MANAGE = 'Only owners and property managers can change alerts';
const MAX_SLOTS = 3;

const SECTIONS: Array<[AlertBooleanKey, string]> = [
  ['show_current_stay', 'Currently staying'],
  ['show_departures', 'Departing today'],
  ['show_arrivals', 'Arriving today'],
  ['show_next_reservation', 'Next stay'],
  ['show_extras_due', 'Extras due'],
  ['show_cleaning_today', 'Cleaning today'],
  ['show_turnaround', 'Same-day turnaround'],
];

const DETAILS: Array<[AlertBooleanKey, string]> = [
  ['show_guest_name', 'Guest first name'],
  ['show_source', 'Booking source'],
  ['show_price', 'Price'],
  ['show_nights', 'Number of nights'],
  ['show_arrival_time', 'Arrival time'],
  ['show_checkout_time', 'Checkout time'],
  ['show_daily_totals', 'Daily totals'],
  ['show_quiet_day_note', 'Quiet-day note'],
];

const CHECKOUT_ALERTS: Array<[AlertBooleanKey, string]> = [
  ['notify_scheduled_cleaning', 'Cleaning is booked'],
  ['notify_unscheduled_cleaning', 'Cleaning is not booked'],
  ['notify_same_day_turnaround', 'Same-day turnaround'],
];

const STAY_ALERTS: Array<[AlertBooleanKey, string]> = [
  ['notify_guest_check_in', 'Guest check-in'],
  ['notify_new_reservations', 'New stay'],
  ['notify_cancellations', 'Stay cancelled'],
];

const TEMPLATE_COLUMN: Record<AlertTemplateKind, keyof AlertSettings> = {
  guest_arrived: 'template_guest_arrived',
  cleaning_scheduled: 'template_cleaning_scheduled',
  cleaning_unscheduled: 'template_cleaning_unscheduled',
  turnaround: 'template_turnaround',
};
const KIND_OF_COLUMN: Record<string, AlertTemplateKind> = Object.fromEntries(
  Object.entries(TEMPLATE_COLUMN).map(([k, c]) => [c, k as AlertTemplateKind])
);

type Editable = Omit<AlertSettings, 'listing_id' | 'building_id' | 'version' | 'updated_at'>;

const NOT_EDITABLE = ['listing_id', 'building_id', 'version', 'updated_at'];

const editableOf = (s: AlertSettings): Editable =>
  Object.fromEntries(Object.entries(s).filter(([k]) => !NOT_EDITABLE.includes(k))) as Editable;

/** The keys whose value differs (arrays compared by value). */
export const changedKeys = (a: Editable, b: Editable): Array<keyof Editable> =>
  (Object.keys(b) as Array<keyof Editable>).filter(k => JSON.stringify(a[k]) !== JSON.stringify(b[k]));

const summaryToast = (r: { status: string; recipients: number }): string =>
  r.status === 'sent'
    ? `Summary sent to ${r.recipients} ${r.recipients === 1 ? 'person' : 'people'}`
    : 'Nothing to report today — no summary sent';

export const AlertsTab: React.FC<{ listing: StaysListingHead; canManage: boolean }> = ({ listing, canManage }) => {
  const [view, setView] = useState<'loading' | 'error' | 'ready'>('loading');
  const [state, setState] = useState<AlertSettingsState | null>(null);
  const [form, setForm] = useState<Editable | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [stale, setStale] = useState(false);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [templateError, setTemplateError] = useState<{ kind: AlertTemplateKind; message: string } | null>(null);
  const [sendingNow, setSendingNow] = useState(false);

  const read = useCallback(async () => {
    setView('loading');
    try {
      const s = await getAlertSettings(listing.id);
      setState(s);
      setForm(editableOf(s.settings));
      setStale(false);
      setFields({});
      setTemplateError(null);
      setView('ready');
    } catch {
      setView('error');
    }
  }, [listing.id]);

  useEffect(() => {
    void read();
  }, [read]);

  const dirtyKeys = useMemo(
    () => (state && form ? changedKeys(editableOf(state.settings), form) : []),
    [state, form]
  );

  const set = <K extends keyof Editable>(key: K, value: Editable[K]) => {
    setSaved(false);
    setForm(f => (f ? { ...f, [key]: value } : f));
  };

  const drafts = useMemo(() => {
    const out = {} as Record<AlertTemplateKind, string | null>;
    if (form) for (const [k, c] of Object.entries(TEMPLATE_COLUMN)) out[k as AlertTemplateKind] = form[c as keyof Editable] as string | null;

    return out;
  }, [form]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!state || !form || saving || !dirtyKeys.length) return;
    setSaving(true);
    setFields({});
    setTemplateError(null);
    const patch: Record<string, unknown> = { version: state.settings.version };
    for (const k of dirtyKeys) patch[k] = form[k];
    try {
      const out = await saveAlertSettings(listing.id, patch as Parameters<typeof saveAlertSettings>[1]);
      setState({ ...state, settings: out.settings });
      setForm(editableOf(out.settings));
      setSaved(true);
      toast.success('Alert settings saved');
    } catch (err) {
      const x = err as StaysApiError;
      if (x && x.code === 'STAYS_SETTINGS_STALE') setStale(true);
      else if (x && x.field && KIND_OF_COLUMN[x.field]) setTemplateError({ kind: KIND_OF_COLUMN[x.field], message: x.message });
      else if (x && x.code === 'SUMMARY_SLOTS_INVALID') setFields({ summary_slots: x.message });
      else if (x && x.status === 400 && Object.keys(x.fields || {}).length) setFields(x.fields);
      else toast.error(refusalText(x, 'Something went wrong. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const summaryNow = async () => {
    if (sendingNow) return;
    setSendingNow(true);
    try {
      toast.success(summaryToast(await sendSummaryNow(listing.id)));
    } catch (err) {
      toast.error(refusalText(err as StaysApiError, 'The summary could not be sent.'));
    } finally {
      setSendingNow(false);
    }
  };

  const chips = (list: Array<[AlertBooleanKey, string]>, disabled = false) =>
    form
      ? list.map(([key, label]) => (
          <ToggleChip key={key} pressed={!!form[key]} disabled={!canManage || disabled} onToggle={next => set(key, next)}>
            {label}
          </ToggleChip>
        ))
      : null;

  const clock = buildingClock(state ? state.building.timezone : listing.timezone);
  const slots = form ? form.summary_slots : [];
  const masterOff = !!form && !form.auto_cleaning_alerts;

  return (
    <StateSwitch state={view}>
      {view === 'loading' ? <FormSkeleton fields={6} label="alert settings" /> : null}
      {view === 'error' ? <LoadError message="Couldn't load alert settings" onRetry={() => void read()} /> : null}
      {view === 'ready' && state && form ? (
        <Page onSubmit={save} noValidate aria-label="Alert settings" data-testid="alerts-tab">
          <Columns>
            <Column>
              <Card aria-labelledby="alerts-summary-title">
                <CardTitle id="alerts-summary-title">Daily summary</CardTitle>
                <Check htmlFor={`summary-on-${listing.id}`}>
                  <input id={`summary-on-${listing.id}`} type="checkbox" checked={form.summary_enabled} disabled={!canManage}
                    onChange={e => set('summary_enabled', e.target.checked)} />
                  <span>Send a daily summary</span>
                </Check>
                <GroupLabel id={`summary-slots-${listing.id}`}>Send times</GroupLabel>
                {slots.map((slot, i) => (
                  <SlotRow key={`${i}-${slot}`}>
                    <Input
                      type="time"
                      step={60}
                      aria-label={`Send time ${i + 1}`}
                      value={slot}
                      readOnly={!canManage}
                      hasError={!!fields.summary_slots}
                      onChange={e => set('summary_slots', slots.map((s, j) => (j === i ? e.target.value : s)))}
                    />
                    {canManage && slots.length > 1 ? (
                      <AppButton type="button" variant="ghost" size="compact" aria-label={`Remove the ${slot} send time`}
                        onClick={() => set('summary_slots', slots.filter((_, j) => j !== i))}>
                        Remove
                      </AppButton>
                    ) : null}
                  </SlotRow>
                ))}
                {canManage && slots.length < MAX_SLOTS ? (
                  <AppButton type="button" variant="secondary" onClick={() => set('summary_slots', [...slots, '14:00'])}>
                    Add a time
                  </AppButton>
                ) : null}
                <FieldError message={fields.summary_slots} />
                <ClockNote clock={clock} label={state.building.zone_note} />
                <Field>
                  <Label htmlFor={`summary-greeting-${listing.id}`}>Greeting (optional)</Label>
                  <Input
                    id={`summary-greeting-${listing.id}`}
                    value={form.summary_greeting}
                    maxLength={60}
                    readOnly={!canManage}
                    hasError={!!fields.summary_greeting}
                    onChange={e => set('summary_greeting', e.target.value)}
                    aria-describedby={`summary-greeting-hint-${listing.id}`}
                  />
                  <FieldHint id={`summary-greeting-hint-${listing.id}`}>Shown at the top of the email</FieldHint>
                  <FieldError message={fields.summary_greeting} />
                </Field>
                <GroupLabel>Sections</GroupLabel>
                <ChipGroup aria-label="Summary sections">{chips(SECTIONS)}</ChipGroup>
                <GroupLabel>Information included</GroupLabel>
                <ChipGroup aria-label="Information included">{chips(DETAILS)}</ChipGroup>
                {canManage ? (
                  <AppButton type="button" variant="secondary" onClick={() => void summaryNow()} loading={sendingNow}
                    loadingLabel="Sending…">
                    Send today&apos;s summary now
                  </AppButton>
                ) : null}
              </Card>
              <Card aria-labelledby="alerts-alerts-title">
                <CardTitle id="alerts-alerts-title">Alerts</CardTitle>
                <GroupLabel>Checkout alerts</GroupLabel>
                <Check htmlFor={`auto-cleaning-${listing.id}`}>
                  <input id={`auto-cleaning-${listing.id}`} type="checkbox" checked={form.auto_cleaning_alerts}
                    disabled={!canManage} onChange={e => set('auto_cleaning_alerts', e.target.checked)} />
                  <span>Send checkout and cleaning alerts</span>
                </Check>
                <ActionWithReason reason={masterOff ? 'Turn on checkout and cleaning alerts to choose these.' : null}>
                  {describedBy => (
                    <ChipGroup aria-label="Checkout alerts" aria-describedby={describedBy}>
                      {chips(CHECKOUT_ALERTS, masterOff)}
                    </ChipGroup>
                  )}
                </ActionWithReason>
                <GroupLabel>Stay alerts</GroupLabel>
                <ChipGroup aria-label="Stay alerts">{chips(STAY_ALERTS)}</ChipGroup>
              </Card>
            </Column>
            <Column>
              <TemplateStudio
                listingId={listing.id}
                canManage={canManage}
                labels={state.labels}
                descriptions={state.descriptions}
                placeholders={state.placeholders}
                defaults={state.default_templates}
                drafts={drafts}
                onDraft={(kind, text) => {
                  setTemplateError(null);
                  set(TEMPLATE_COLUMN[kind] as keyof Editable, text as never);
                }}
                serverError={templateError}
              />
              <AlertHistory listingId={listing.id} initial={state.recent} />
            </Column>
          </Columns>
          <Footer $float={dirtyKeys.length > 0 || stale || saving || saved}>
            <ActionWithReason reason={canManage ? null : NO_MANAGE}>
              {describedBy => (
                <AppButton type="submit" variant="primary" loading={saving} loadingLabel="Saving" success={saved}
                  successLabel="Saved" disabled={!canManage || !dirtyKeys.length} aria-describedby={describedBy}>
                  Save alert settings
                </AppButton>
              )}
            </ActionWithReason>
            <InlineMessage role="status" message={dirtyKeys.length && !stale ? 'You have unsaved changes' : null} />
            <InlineMessage role="alert" message={stale ? 'Someone else changed these settings. Reload to see their version.' : null} />
            {stale ? (
              <AppButton type="button" variant="secondary" onClick={() => void read()}>
                Reload
              </AppButton>
            ) : null}
          </Footer>
        </Page>
      ) : null}
    </StateSwitch>
  );
};
